// Settings — User Profile tab. Settings-02.
//
// Surfaces the current user's name, email, and brokerage membership
// alongside a "Change password" action. Clicking the action requests
// a branded password-change email through
// ``/api/v1/auth/password-change-link``; the actual reset happens on
// the public ``/reset-password?token=…`` route. We deliberately do
// not collect a current password in-app — the email link is the
// security boundary.

import { useContext, useState } from "react";

import { Button } from "@/design-system";
import { AuthContext } from "@/features/auth/AuthContext";

import { useRequestPasswordChangeLinkMutation } from "./hooks";


export function UserProfileTab() {
  const auth = useContext(AuthContext);
  const user = auth?.user ?? null;
  const membership = auth?.currentMembership ?? null;
  const linkMutation = useRequestPasswordChangeLinkMutation();
  const [success, setSuccess] = useState(false);

  const initials = computeInitials(
    user?.display_name || user?.email || "",
  );

  const onChangePassword = () => {
    setSuccess(false);
    linkMutation.mutate(undefined, {
      onSuccess: () => setSuccess(true),
    });
  };

  return (
    <div data-testid="settings-user-profile" className="flex flex-col gap-5">
      <section className="flex items-start gap-4 rounded-lg border border-border-subtle bg-canvas/40 p-4">
        <span
          aria-hidden="true"
          className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-sage-tint text-sm font-semibold text-teal"
        >
          {initials}
        </span>
        <div className="flex flex-col gap-1">
          <p
            data-testid="settings-user-profile-name"
            className="text-sm font-semibold text-text-primary"
          >
            {user?.display_name || user?.email || "Signed-in user"}
          </p>
          <p
            data-testid="settings-user-profile-email"
            className="text-xs text-text-secondary"
          >
            {user?.email}
          </p>
          {membership && (
            <p
              data-testid="settings-user-profile-entity"
              className="text-xs text-text-tertiary"
            >
              {membership.entity.name}
              {membership.roles.length > 0
                ? ` · ${membership.roles.map(prettyRole).join(", ")}`
                : ""}
            </p>
          )}
        </div>
      </section>

      <section
        data-testid="settings-user-profile-password-section"
        className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-canvas/40 p-4"
      >
        <header>
          <h2 className="text-sm font-semibold text-text-primary">
            Password
          </h2>
          <p className="mt-1 max-w-xl text-xs text-text-secondary">
            We&rsquo;ll email you a secure link to change your password.
          </p>
        </header>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            data-testid="settings-user-profile-change-password"
            onClick={onChangePassword}
            disabled={linkMutation.isPending}
          >
            {linkMutation.isPending ? "Sending…" : "Change password"}
          </Button>
          {success && (
            <p
              role="status"
              data-testid="settings-user-profile-password-success"
              className="text-xs font-medium text-teal"
            >
              Password change email sent. Check your inbox.
            </p>
          )}
          {linkMutation.isError && !success && (
            <p
              role="alert"
              data-testid="settings-user-profile-password-error"
              className="text-xs font-medium text-ember"
            >
              Couldn&rsquo;t send the email. Please try again.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}


function computeInitials(name: string): string {
  const cleaned = name.trim();
  if (!cleaned) return "?";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return cleaned.slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}


function prettyRole(role: string): string {
  if (!role) return "";
  return role
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
